import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import { Avatar, Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from "@mui/material";
import type { FC } from "react";
import { Fragment } from "react";
import type { SurveyQuestion, SurveyResponse } from "./staffSatisfactionTypes";

interface QuestionDetailModalProps {
	open: boolean;
	onClose: () => void;
	question: SurveyQuestion | null;
	responses: SurveyResponse[];
}

export const QuestionDetailModal: FC<QuestionDetailModalProps> = ({ open, onClose, question, responses }) => {
	if (!question) return null;

	const sortedResponses = [...responses].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

	const formatDate = (isoString: string) =>
		new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(isoString));

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			slotProps={{
				paper: { sx: { borderRadius: 3, maxHeight: "85vh" } },
			}}
		>
			<DialogTitle sx={{ m: 0, p: 3, pr: 6 }}>
				<Typography
					variant="h5"
					sx={{
						fontWeight: "bold",
						lineHeight: 1.3,
					}}
				>
					{question.text}
				</Typography>
				<IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 16, top: 16, color: "text.secondary" }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ p: 0 }}>
				<Box sx={{ bgcolor: "primary.50", p: 3, borderBottom: "1px solid", borderColor: "primary.100" }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
						<AutoAwesomeIcon sx={{ color: "primary.main" }} />
						<Typography
							variant="subtitle1"
							sx={{
								fontWeight: "bold",
								color: "primary.900",
							}}
						>
							{"AI Summary"}
						</Typography>
					</Box>
					<Typography
						variant="body1"
						sx={{
							color: "text.secondary",
							lineHeight: 1.6,
						}}
					>
						{question.aiSummary}
					</Typography>
				</Box>

				<Box sx={{ p: 3 }}>
					<Typography
						variant="h6"
						gutterBottom
						sx={{
							fontWeight: 600,
							mb: 3,
						}}
					>
						{`Individual Responses (${responses.length})`}
					</Typography>

					<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
						{sortedResponses.map((response, index) => (
							<Fragment key={response.id}>
								<Box sx={{ display: "flex", gap: 2 }}>
									<Avatar sx={{ bgcolor: "grey.200", color: "grey.700", width: 40, height: 40 }}>{response.staffName.charAt(0)}</Avatar>
									<Box sx={{ flexGrow: 1 }}>
										<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<Typography
													variant="subtitle2"
													sx={{
														fontWeight: "bold",
													}}
												>
													{response.staffName}
												</Typography>
												<Typography
													variant="caption"
													sx={{
														bgcolor: "primary.50",
														color: "primary.main",
														fontWeight: 700,
														px: 0.75,
														py: 0.25,
														borderRadius: 0.75,
														fontSize: "0.7rem",
														letterSpacing: "0.3px",
													}}
												>
													{response.role}
												</Typography>
											</Box>
											<Typography
												variant="caption"
												sx={{
													color: "text.secondary",
												}}
											>
												{formatDate(response.timestamp)}
											</Typography>
										</Box>
										<Typography
											variant="body2"
											sx={{
												color: "text.primary",
												whiteSpace: "pre-wrap",
											}}
										>
											{response.responseText}
										</Typography>
									</Box>
								</Box>
								{index < sortedResponses.length - 1 && <Divider />}
							</Fragment>
						))}

						{sortedResponses.length === 0 && (
							<Typography
								variant="body2"
								sx={{
									color: "text.secondary",
									textAlign: "center",
									py: 4,
								}}
							>
								{"No responses recorded for this question yet."}
							</Typography>
						)}
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
